import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Users, Package, Wrench, AlertTriangle, 
  TrendingUp, TrendingDown, Calendar, ChevronRight, Building2,
  ShieldCheck, ShieldX, Clock, Truck, X, ExternalLink,
  ChevronUp, ChevronDown, BarChart3, ShoppingBag, RefreshCw, Loader2
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const StatCard = ({ title, value, icon: Icon, trend, color = 'green', link }) => {
  const colorClasses = {
    green: 'text-[#00E01A] bg-[#142915]',
    red: 'text-[#E74C3C] bg-[#2A1515]',
    yellow: 'text-[#F39C12] bg-[#2A2010]',
    blue: 'text-[#3498DB] bg-[#152A35]',
  };

  const content = (
    <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5 hover:border-[#00E01A]/30 transition-all duration-200 group cursor-pointer">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[#8C8F94] text-sm mb-1">{title}</p>
          <p className="text-3xl font-bold text-white font-['Outfit']">{value}</p>
          {trend && (
            <p className={`text-xs mt-2 ${trend > 0 ? 'text-[#00E01A]' : 'text-[#E74C3C]'}`}>
              {trend > 0 ? '+' : ''}{trend}% ce mois
            </p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon size={24} />
        </div>
      </div>
      {link && (
        <div className="mt-4 pt-3 border-t border-[#2C3338] flex items-center justify-between text-sm text-[#8C8F94] group-hover:text-[#00E01A] transition-colors">
          <span>Voir détails</span>
          <ChevronRight size={16} className="transform group-hover:translate-x-1 transition-transform" />
        </div>
      )}
    </div>
  );

  return link ? <Link to={link} className="block">{content}</Link> : content;
};

const AlertItem = ({ title, description, type, link }) => {
  const typeClasses = {
    warning: 'border-l-[#F39C12] bg-[#2A2010]/50',
    danger: 'border-l-[#E74C3C] bg-[#2A1515]/50',
    info: 'border-l-[#3498DB] bg-[#152A35]/50',
  };

  const content = (
    <div className={`border-l-4 ${typeClasses[type]} rounded-r-lg p-4 hover:bg-opacity-80 transition-colors cursor-pointer`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white font-medium text-sm">{title}</p>
          <p className="text-[#8C8F94] text-xs mt-1">{description}</p>
        </div>
        <ChevronRight size={16} className="text-[#8C8F94]" />
      </div>
    </div>
  );

  return link ? <Link to={link} className="block">{content}</Link> : content;
};

const FranchiseCard = ({ franchise, onClick }) => (
  <div 
    className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-4 hover:border-[#00E01A]/30 transition-colors cursor-pointer group"
    onClick={() => onClick(franchise)}
    data-testid={`franchise-card-${franchise.id}`}
  >
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-[#142915]">
          <Building2 size={18} className="text-[#00E01A]" />
        </div>
        <div>
          <p className="text-white font-medium">{franchise.name}</p>
          <p className="text-[#8C8F94] text-xs">{franchise.city}</p>
        </div>
      </div>
      <ChevronRight size={16} className="text-[#8C8F94] group-hover:text-[#00E01A] transition-colors" />
    </div>
    <div className="grid grid-cols-3 gap-2 text-center">
      <div className="bg-[#141617] rounded-lg p-2">
        <p className="text-white font-bold">{franchise.clients}</p>
        <p className="text-[#8C8F94] text-xs">Clients</p>
      </div>
      <div className="bg-[#141617] rounded-lg p-2">
        <p className="text-white font-bold">{franchise.open_sav}</p>
        <p className="text-[#8C8F94] text-xs">SAV</p>
      </div>
      <div className="bg-[#141617] rounded-lg p-2">
        <p className="text-[#00E01A] font-bold text-sm">{(franchise.total_ca / 1000).toFixed(0)}k€</p>
        <p className="text-[#8C8F94] text-xs">CA</p>
      </div>
    </div>
  </div>
);

const FranchiseDetailModal = ({ franchise, onClose }) => {
  const navigate = useNavigate();
  
  if (!franchise) return null;

  return (
    <Dialog open={!!franchise} onOpenChange={() => onClose()}>
      <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold font-['Outfit'] flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#142915]">
              <Building2 size={20} className="text-[#00E01A]" />
            </div>
            {franchise.name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-[#8C8F94]">{franchise.city}</p>
          
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-[#141617] rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-white">{franchise.clients}</p>
              <p className="text-[#8C8F94] text-sm">Clients</p>
            </div>
            <div className="bg-[#141617] rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-[#F39C12]">{franchise.open_sav}</p>
              <p className="text-[#8C8F94] text-sm">SAV ouverts</p>
            </div>
            <div className="bg-[#141617] rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-[#00E01A]">{(franchise.total_ca / 1000).toFixed(0)}k€</p>
              <p className="text-[#8C8F94] text-sm">CA total</p>
            </div>
          </div>

          <div className="pt-4 border-t border-[#2C3338] space-y-2">
            <button
              onClick={() => { onClose(); navigate(`/clients?franchise_id=${franchise.id}`); }}
              className="w-full flex items-center justify-between p-3 bg-[#141617] rounded-lg hover:bg-[#2C3338] transition-colors"
              data-testid="franchise-view-clients"
            >
              <span className="flex items-center gap-2">
                <Users size={16} className="text-[#00E01A]" />
                <span>Voir les clients</span>
              </span>
              <ChevronRight size={16} className="text-[#8C8F94]" />
            </button>
            <button
              onClick={() => { onClose(); navigate(`/sav?franchise_id=${franchise.id}`); }}
              className="w-full flex items-center justify-between p-3 bg-[#141617] rounded-lg hover:bg-[#2C3338] transition-colors"
              data-testid="franchise-view-sav"
            >
              <span className="flex items-center gap-2">
                <Wrench size={16} className="text-[#F39C12]" />
                <span>Voir les tickets SAV</span>
              </span>
              <ChevronRight size={16} className="text-[#8C8F94]" />
            </button>
            <button
              onClick={() => { onClose(); navigate(`/inventory?franchise_id=${franchise.id}`); }}
              className="w-full flex items-center justify-between p-3 bg-[#141617] rounded-lg hover:bg-[#2C3338] transition-colors"
              data-testid="franchise-view-inventory"
            >
              <span className="flex items-center gap-2">
                <Package size={16} className="text-[#3498DB]" />
                <span>Voir l'inventaire</span>
              </span>
              <ChevronRight size={16} className="text-[#8C8F94]" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload) return null;
  return (
    <div className="bg-[#1D2327] border border-[#2C3338] rounded-lg p-3 shadow-xl">
      <p className="text-white font-semibold text-sm mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-sm" style={{ color: entry.color }}>
          {entry.name}: {Number(entry.value).toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€
        </p>
      ))}
    </div>
  );
};

const SalesStats = ({ api }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedYear, setSelectedYear] = useState('2025');
  const [compareYear, setCompareYear] = useState('2026');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [monthDetail, setMonthDetail] = useState(null);
  const [monthLoading, setMonthLoading] = useState(false);
  const [monthYear, setMonthYear] = useState(null);

  const fetchSalesStats = async () => {
    try {
      const res = await api.get('/axonaut/sales-stats');
      setStats(res.data);
      const years = res.data.available_years || [];
      if (years.length >= 2) {
        setSelectedYear(years[years.length - 2]);
        setCompareYear(years[years.length - 1]);
        setMonthYear(years[years.length - 1]);
      } else if (years.length === 1) {
        setSelectedYear(years[0]);
        setCompareYear(years[0]);
        setMonthYear(years[0]);
      }
    } catch (e) {
      console.error('Failed to fetch sales stats:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesStats();
  }, [api]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.post('/axonaut/sync');
      // Wait briefly then refresh stats
      await new Promise(r => setTimeout(r, 3000));
      await fetchSalesStats();
    } catch (e) {
      console.error('Sync error:', e);
    } finally {
      setSyncing(false);
    }
  };

  const handleBarClick = async (monthIdx) => {
    const year = monthYear || compareYear;

    if (selectedMonth && selectedMonth.year === year && selectedMonth.month === monthIdx) {
      setSelectedMonth(null);
      setMonthDetail(null);
      return;
    }

    setSelectedMonth({ year, month: monthIdx });
    setMonthLoading(true);
    setMonthDetail(null);
    try {
      const res = await api.get(`/axonaut/month-detail?year=${year}&month=${monthIdx}`);
      setMonthDetail(res.data);
    } catch (e) {
      console.error('Month detail error:', e);
    } finally {
      setMonthLoading(false);
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      await api.put(`/axonaut/invoice/${invoiceId}/status`, { payment_status: newStatus });
      // Refresh month detail
      if (selectedMonth) {
        const res = await api.get(`/axonaut/month-detail?year=${selectedMonth.year}&month=${selectedMonth.month}`);
        setMonthDetail(res.data);
      }
      // Refresh main stats
      const statsRes = await api.get('/axonaut/sales-stats');
      setStats(statsRes.data);
    } catch (e) {
      console.error('Status update error:', e);
    }
  };

  if (loading || !stats) return null;

  const yearData = stats.years || {};
  const selCA = yearData[selectedYear]?.ca_ttc || 0;
  const cmpCA = yearData[compareYear]?.ca_ttc || 0;
  const selCount = yearData[selectedYear]?.count || 0;
  const cmpCount = yearData[compareYear]?.count || 0;

  const chartData = (stats.monthly_comparison || []).map((m, idx) => ({
    name: m.month_name,
    monthIdx: m.month,
    [selectedYear]: m[selectedYear] || 0,
    [compareYear]: m[compareYear] || 0,
  }));

  const currentMonth = new Date().getMonth();
  const ytdSelected = chartData.slice(0, currentMonth + 1).reduce((s, m) => s + (m[selectedYear] || 0), 0);
  const ytdCompare = chartData.slice(0, currentMonth + 1).reduce((s, m) => s + (m[compareYear] || 0), 0);
  const ytdDiff = ytdSelected > 0 ? ((ytdCompare - ytdSelected) / ytdSelected * 100) : 0;

  return (
    <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] overflow-hidden" data-testid="sales-stats-section">
      <div className="p-4 md:p-5">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-[#142915] flex items-center justify-center">
            <BarChart3 size={20} className="text-[#00E01A]" />
          </div>
          <div className="flex-1">
            <h2 className="text-white font-bold font-['Outfit'] text-lg">Statistiques de vente</h2>
            <p className="text-[#8C8F94] text-sm">Axonaut — {stats.available_years?.join(', ')}</p>
          </div>
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#142915] border border-[#00E01A]/30 text-[#00E01A] hover:bg-[#00E01A]/15 transition-all text-sm font-medium disabled:opacity-50"
            data-testid="sync-sales-btn"
          >
            {syncing ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <RefreshCw size={16} />
            )}
            {syncing ? 'Synchronisation...' : 'Synchroniser'}
          </button>
        </div>

        <div className="space-y-5">
          {/* Year selectors */}
          <div className="flex items-center gap-3">
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-32 bg-[#141617] border-[#2C3338] text-sm" data-testid="stats-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(stats.available_years || []).map(y => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-[#8C8F94] text-sm">vs</span>
            <Select value={compareYear} onValueChange={setCompareYear}>
              <SelectTrigger className="w-32 bg-[#141617] border-[#2C3338] text-sm" data-testid="stats-compare-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(stats.available_years || []).map(y => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-[#141617] rounded-xl p-4">
              <p className="text-[#8C8F94] text-xs mb-1 uppercase tracking-wide">CA {selectedYear}</p>
              <p className="text-white font-bold text-xl">{selCA.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€</p>
              <p className="text-[#8C8F94] text-xs mt-1">{selCount} factures</p>
            </div>
            <div className="bg-[#141617] rounded-xl p-4">
              <p className="text-[#8C8F94] text-xs mb-1 uppercase tracking-wide">CA {compareYear}</p>
              <p className="text-[#00E01A] font-bold text-xl">{cmpCA.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€</p>
              <p className="text-[#8C8F94] text-xs mt-1">{cmpCount} factures</p>
            </div>
            <div className="bg-[#141617] rounded-xl p-4">
              <p className="text-[#8C8F94] text-xs mb-1 uppercase tracking-wide">YTD {compareYear} vs {selectedYear}</p>
              <div className="flex items-center gap-2">
                {ytdDiff >= 0 ? (
                  <TrendingUp size={18} className="text-[#00E01A]" />
                ) : (
                  <TrendingDown size={18} className="text-[#E74C3C]" />
                )}
                <p className={`font-bold text-xl ${ytdDiff >= 0 ? 'text-[#00E01A]' : 'text-[#E74C3C]'}`}>
                  {ytdDiff >= 0 ? '+' : ''}{ytdDiff.toFixed(1)}%
                </p>
              </div>
              <p className="text-[#8C8F94] text-xs mt-1">Jan–{chartData[currentMonth]?.name}</p>
            </div>
            <div className="bg-[#141617] rounded-xl p-4">
              <p className="text-[#8C8F94] text-xs mb-1 uppercase tracking-wide">Payé {compareYear}</p>
              <p className="text-white font-bold text-xl">
                {(yearData[compareYear]?.paid || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€
              </p>
              <p className="text-[#E74C3C] text-xs mt-1">
                Impayé: {(yearData[compareYear]?.unpaid || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€
              </p>
            </div>
          </div>

          {/* Monthly comparison chart - CLICKABLE */}
          <div className="bg-[#141617] rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold text-sm">CA mensuel — {selectedYear} vs {compareYear}</h3>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2C3338" />
                  <XAxis dataKey="name" tick={{ fill: '#8C8F94', fontSize: 12 }} axisLine={{ stroke: '#2C3338' }} />
                  <YAxis
                    tick={{ fill: '#8C8F94', fontSize: 11 }}
                    axisLine={{ stroke: '#2C3338' }}
                    tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    wrapperStyle={{ paddingTop: '8px' }}
                    formatter={(value) => <span style={{ color: '#8C8F94', fontSize: '12px' }}>{value}</span>}
                  />
                  <Bar dataKey={selectedYear} fill="#52555A" radius={[4, 4, 0, 0]} maxBarSize={32} name={selectedYear} />
                  <Bar dataKey={compareYear} fill="#00E01A" radius={[4, 4, 0, 0]} maxBarSize={32} name={compareYear} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Year selector + Clickable month buttons */}
            <div className="mt-3 pt-3 border-t border-[#2C3338]">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[#52555A] text-xs">Détail mensuel :</span>
                {(stats.available_years || []).map((y) => (
                  <button
                    key={y}
                    onClick={() => { setMonthYear(y); setSelectedMonth(null); setMonthDetail(null); }}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                      (monthYear || compareYear) === y
                        ? 'bg-[#00E01A]/15 text-[#00E01A] border border-[#00E01A]/40'
                        : 'bg-[#1D2327] text-[#8C8F94] border border-[#2C3338] hover:text-white'
                    }`}
                    data-testid={`detail-year-${y}`}
                  >
                    {y}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-6 md:grid-cols-12 gap-1.5">
                {(stats.monthly_comparison || []).map((m) => {
                  const yr = monthYear || compareYear;
                  const isActive = selectedMonth && selectedMonth.year === yr && selectedMonth.month === m.month;
                  const val = m[yr] || 0;
                  const hasData = val > 0;
                  return (
                    <button
                      key={m.month}
                      onClick={() => hasData && handleBarClick(m.month)}
                      disabled={!hasData}
                      className={`py-2 px-1 rounded-lg text-center transition-all ${
                        isActive
                          ? 'bg-[#00E01A]/15 border border-[#00E01A]/40 text-[#00E01A]'
                          : hasData
                            ? 'bg-[#1D2327] border border-[#2C3338] text-[#8C8F94] hover:border-[#00E01A]/30 hover:text-white cursor-pointer'
                            : 'bg-[#1D2327]/50 border border-[#2C3338]/30 text-[#2C3338] cursor-default'
                      }`}
                      data-testid={`month-btn-${m.month}`}
                    >
                      <p className="text-[10px] font-semibold">{m.month_name}</p>
                      <p className={`text-[10px] font-mono ${isActive ? 'text-[#00E01A]' : hasData ? 'text-[#52555A]' : 'text-[#2C3338]'}`}>
                        {(val / 1000).toFixed(0)}k
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Month detail panel */}
          {monthLoading && (
            <div className="bg-[#141617] rounded-xl p-8 text-center">
              <Clock size={24} className="mx-auto text-[#00E01A] animate-pulse mb-2" />
              <p className="text-[#8C8F94] text-sm">Chargement du détail...</p>
            </div>
          )}

          {monthDetail && !monthLoading && (
            <div className="bg-[#141617] rounded-xl border border-[#00E01A]/20 overflow-hidden" data-testid="month-detail-panel">
              {/* Month detail header */}
              <div className="flex items-center justify-between p-4 border-b border-[#2C3338]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#00E01A]/10 flex items-center justify-center">
                    <Calendar size={20} className="text-[#00E01A]" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-base">{monthDetail.month_name} {monthDetail.year}</h3>
                    <p className="text-[#8C8F94] text-xs">{monthDetail.invoice_count} facture{monthDetail.invoice_count > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-[#00E01A] font-bold text-lg">{monthDetail.total_ca.toLocaleString('fr-FR')}€</p>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-[#00E01A]">Payé: {monthDetail.total_paid.toLocaleString('fr-FR')}€</span>
                      {monthDetail.total_unpaid > 0 && (
                        <span className="text-[#E74C3C]">Impayé: {monthDetail.total_unpaid.toLocaleString('fr-FR')}€</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => { setSelectedMonth(null); setMonthDetail(null); }}
                    className="p-1.5 rounded-lg hover:bg-[#2C3338] text-[#8C8F94] hover:text-white transition-colors"
                    data-testid="close-month-detail"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* Invoices list */}
              <div className="p-4 space-y-3">
                {monthDetail.invoices.map((inv, i) => (
                  <div key={i} className="bg-[#1D2327] rounded-lg border border-[#2C3338] overflow-hidden">
                    <div className="flex items-center justify-between p-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          inv.payment_status === 'paid' ? 'bg-[#00E01A]' :
                          inv.payment_status === 'cancelled' ? 'bg-[#8C8F94]' :
                          inv.payment_status === 'refunded' ? 'bg-[#F39C12]' :
                          'bg-[#E74C3C]'
                        }`} />
                        <div>
                          <p className="text-white font-medium text-sm">{inv.client_name}</p>
                          <p className="text-[#52555A] text-xs font-mono">{inv.invoice_number} — {new Date(inv.date).toLocaleDateString('fr-FR')}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className={`font-semibold ${
                            inv.payment_status === 'cancelled' || inv.payment_status === 'refunded'
                              ? 'text-[#8C8F94] line-through' : 'text-white'
                          }`}>{inv.total_ttc.toLocaleString('fr-FR')}€</p>
                        </div>
                        <select
                          value={inv.payment_status}
                          onChange={(e) => handleStatusChange(inv.id, e.target.value)}
                          className={`text-xs font-medium px-2 py-1 rounded-full border cursor-pointer bg-transparent outline-none ${
                            inv.payment_status === 'paid' ? 'text-[#00E01A] border-[#00E01A]/30' :
                            inv.payment_status === 'cancelled' ? 'text-[#8C8F94] border-[#52555A]/30' :
                            inv.payment_status === 'refunded' ? 'text-[#F39C12] border-[#F39C12]/30' :
                            'text-[#E74C3C] border-[#E74C3C]/30'
                          }`}
                          data-testid={`invoice-status-${i}`}
                        >
                          <option value="paid" className="bg-[#1D2327]">Payée</option>
                          <option value="unpaid" className="bg-[#1D2327]">Impayée</option>
                          <option value="cancelled" className="bg-[#1D2327]">Annulée</option>
                          <option value="refunded" className="bg-[#1D2327]">Remboursée</option>
                        </select>
                      </div>
                    </div>
                    {inv.products.length > 0 && (
                      <div className="border-t border-[#2C3338] px-3 py-2 bg-[#141617]">
                        {inv.products.map((p, j) => (
                          <div key={j} className="flex items-center justify-between py-1 text-xs">
                            <span className="text-[#8C8F94]">{p.quantity}x {p.name}</span>
                            <span className="text-[#8C8F94] font-mono">{p.total_ttc.toLocaleString('fr-FR')}€</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {monthDetail.invoices.length === 0 && (
                  <p className="text-[#8C8F94] text-sm text-center py-4">Aucune facture ce mois-ci</p>
                )}
              </div>

              {/* Product breakdown for this month */}
              {monthDetail.products.length > 0 && (
                <div className="border-t border-[#2C3338] p-4">
                  <h4 className="text-white font-semibold text-sm flex items-center gap-2 mb-3">
                    <ShoppingBag size={14} className="text-[#00E01A]" />
                    Produits vendus ce mois
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {monthDetail.products.slice(0, 10).map((p, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded bg-[#1D2327]">
                        <span className="text-[#8C8F94] text-xs">{p.quantity}x {p.name}</span>
                        <span className="text-white text-xs font-semibold">{p.ca_ttc.toLocaleString('fr-FR')}€</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Default: show when no month selected */}
          {!selectedMonth && !monthLoading && (
            <div className="bg-[#141617] rounded-xl p-4 border border-dashed border-[#2C3338] text-center">
              <Calendar size={32} className="mx-auto text-[#2C3338] mb-2" />
              <p className="text-[#52555A] text-sm">Cliquez sur un mois dans le graphique pour voir le détail des factures</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { api, user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFranchise, setSelectedFranchise] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/dashboard/stats');
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [api]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5 animate-pulse">
              <div className="h-4 w-24 bg-[#2C3338] rounded mb-3"></div>
              <div className="h-8 w-16 bg-[#2C3338] rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">
            Tableau de bord
          </h1>
          <p className="text-[#8C8F94] mt-1">
            Bienvenue, {user?.first_name} {user?.last_name}
            {user?.franchise_name && ` • ${user.franchise_name}`}
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-[#8C8F94]">
          <Calendar size={16} />
          <span>{new Date().toLocaleDateString('fr-FR', { 
            weekday: 'long', 
            day: 'numeric', 
            month: 'long', 
            year: 'numeric' 
          })}</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Clients actifs"
          value={stats?.clients || 0}
          icon={Users}
          color="green"
          link="/clients"
        />
        <StatCard
          title="Équipements"
          value={stats?.equipment || 0}
          icon={Package}
          color="blue"
          link="/inventory"
        />
        <StatCard
          title="SAV en cours"
          value={stats?.sav_open || 0}
          icon={Wrench}
          color="yellow"
          link="/sav"
        />
        <StatCard
          title="Prêts actifs"
          value={stats?.loans_active || 0}
          icon={Truck}
          color="green"
          link="/loans"
        />
      </div>

      {/* Sales Statistics (Axonaut) */}
      <SalesStats api={api} />

      {/* Warranty & Revision Alerts - Now clickable */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Link 
          to="/inventory?warranty_status=expiring_soon"
          className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5 hover:border-[#00E01A]/30 transition-colors cursor-pointer group"
          data-testid="warranty-card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ShieldCheck size={20} className="text-[#00E01A]" />
              <h3 className="text-white font-semibold">Garanties</h3>
            </div>
            <ChevronRight size={16} className="text-[#8C8F94] group-hover:text-[#00E01A] transition-colors" />
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[#8C8F94] text-sm">Expiration prochaine (60j)</span>
              <span className="text-[#F39C12] font-bold">{stats?.warranties_expiring || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[#8C8F94] text-sm">Expirées</span>
              <span className="text-[#E74C3C] font-bold">{stats?.warranties_expired || 0}</span>
            </div>
          </div>
        </Link>

        <Link 
          to="/inventory?revision_due=true"
          className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5 hover:border-[#00E01A]/30 transition-colors cursor-pointer group"
          data-testid="revision-card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Clock size={20} className="text-[#F39C12]" />
              <h3 className="text-white font-semibold">Révisions</h3>
            </div>
            <ChevronRight size={16} className="text-[#8C8F94] group-hover:text-[#00E01A] transition-colors" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[#8C8F94] text-sm">À planifier (30j)</span>
            <span className="text-[#F39C12] font-bold">{stats?.revisions_due || 0}</span>
          </div>
        </Link>

        <Link 
          to="/sav?status=closed"
          className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5 hover:border-[#00E01A]/30 transition-colors cursor-pointer group"
          data-testid="sav-closed-card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp size={20} className="text-[#00E01A]" />
              <h3 className="text-white font-semibold">SAV clôturés</h3>
            </div>
            <ChevronRight size={16} className="text-[#8C8F94] group-hover:text-[#00E01A] transition-colors" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[#8C8F94] text-sm">Total historique</span>
            <span className="text-[#00E01A] font-bold">{stats?.sav_closed || 0}</span>
          </div>
        </Link>
      </div>

      {/* Alerts Section */}
      {(stats?.warranties_expiring > 0 || stats?.revisions_due > 0 || stats?.sav_open > 5) && (
        <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={20} className="text-[#F39C12]" />
            <h3 className="text-white font-semibold">Alertes</h3>
          </div>
          <div className="space-y-3">
            {stats?.warranties_expiring > 0 && (
              <AlertItem
                title={`${stats.warranties_expiring} garantie(s) expirant bientôt`}
                description="Contacter les clients pour proposer des extensions ou upgrades"
                type="warning"
                link="/inventory?warranty_status=expiring_soon"
              />
            )}
            {stats?.revisions_due > 0 && (
              <AlertItem
                title={`${stats.revisions_due} révision(s) à planifier`}
                description="Des équipements nécessitent une révision annuelle"
                type="info"
                link="/inventory?revision_due=true"
              />
            )}
            {stats?.sav_open > 5 && (
              <AlertItem
                title={`${stats.sav_open} tickets SAV en cours`}
                description="Nombre élevé de tickets ouverts"
                type="danger"
                link="/sav"
              />
            )}
          </div>
        </div>
      )}

      {/* Franchise Stats (Super Admin only) - Now clickable */}
      {isSuperAdmin() && stats?.franchise_stats && (
        <div>
          <h2 className="text-xl font-bold text-white font-['Outfit'] mb-4">
            Performance réseau
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.franchise_stats.map((franchise) => (
              <FranchiseCard 
                key={franchise.id} 
                franchise={franchise}
                onClick={setSelectedFranchise}
              />
            ))}
          </div>
        </div>
      )}

      {/* Supplier Stats (Super Admin only) - Now clickable */}
      {isSuperAdmin() && stats?.supplier_stats && (
        <div>
          <h2 className="text-xl font-bold text-white font-['Outfit'] mb-4">
            Par fournisseur
          </h2>
          <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#2C3338]">
                    <th className="text-left p-4 text-[#8C8F94] text-xs font-semibold uppercase tracking-wider">Fournisseur</th>
                    <th className="text-center p-4 text-[#8C8F94] text-xs font-semibold uppercase tracking-wider">Produits</th>
                    <th className="text-center p-4 text-[#8C8F94] text-xs font-semibold uppercase tracking-wider">SAV ouvert</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {stats.supplier_stats.map((supplier) => (
                    <tr 
                      key={supplier.id} 
                      className="border-b border-[#2C3338] last:border-0 hover:bg-[#141617]/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/sav?supplier_id=${supplier.id}`)}
                      data-testid={`supplier-row-${supplier.id}`}
                    >
                      <td className="p-4 text-white font-medium">{supplier.name}</td>
                      <td className="p-4 text-center text-[#8C8F94]">{supplier.products}</td>
                      <td className="p-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          supplier.open_sav > 0 
                            ? 'bg-[#2A2010] text-[#F39C12]' 
                            : 'bg-[#142915] text-[#00E01A]'
                        }`}>
                          {supplier.open_sav}
                        </span>
                      </td>
                      <td className="p-4">
                        <ChevronRight size={16} className="text-[#8C8F94]" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Franchise Detail Modal */}
      <FranchiseDetailModal 
        franchise={selectedFranchise}
        onClose={() => setSelectedFranchise(null)}
      />
    </div>
  );
};

export default Dashboard;
